import { useCallback, useState } from 'react';

import { EntryDetails } from '@/api/entries/types/types';
import { checkIfRegularProjection } from '@/feature/lobby/utils/lineupsUtils';
import { getMultiplier } from '@/hooks/use-entries-utils';
import { Product } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { Outcome, Status } from '@/types/api.generated';
import { Defined } from '@/types/utils';
import { isEventAcceptingBets } from '@/utils/filterEvents';
import { logger } from '@/utils/logging';
import { useClient } from 'urql';

import {
    GetSharedEntryDocument,
    GetSharedEntryQuery,
    GetSharedEntryQueryVariables,
    useCreateShareableEntryMutation,
} from '../api/query.generated';
import { useAddLineup } from '../screens/AddLineupModalProvider';
import { CreateLinkResponse, SocialLink, createSocialLink } from '../utils/social-link';
import { useHandleSocialLink, useLineupUnavailableSheet } from './use-share-link-import';

const LOG_TAG = '[FantasyLink]';

export const useFantasyEntryShare = () => {
    const [, createShareableEntry] = useCreateShareableEntryMutation();
    const [creatingLink, setCreatingLink] = useState(false);
    const [shareLink, setShareLink] = useState<string | undefined>(undefined);

    const createFantasyShareLink = useCallback(
        async (entry: Defined<EntryDetails>): Promise<CreateLinkResponse> => {
            try {
                setCreatingLink(true);
                const response = await createShareableEntry({ entryId: entry.id });
                const publicId = response.data?.createSharedEntry?.id;
                if (publicId) {
                    const multiplier = getMultiplier(entry, 'initial');
                    const title = `${entry.picks.length} picks for ${multiplier}`;
                    const desc = entry.picks
                        .slice(0, 4)
                        .map(p => {
                            const isRegularProjection = checkIfRegularProjection(p.projection.type);
                            const projectionValue = isRegularProjection
                                ? p.projection.value
                                : p.projection.nonRegularValue;
                            return `• ${p.player.firstName} ${p.player.lastName} ${
                                p.outcome === Outcome.Less ? '↓' : '↑'
                            } ${projectionValue} ${p.projection.name}`;
                        })
                        .join('\n');
                    const { profile } = useUserStore.getState();
                    const link = await createSocialLink({
                        payload: { entryId: publicId },
                        user_id: profile.sub,
                        user_email: profile.email,
                        type: 'fantasy',
                        meta: { title, desc },
                    });
                    setShareLink(link.shortUrl);
                    return link;
                } else {
                    throw new Error('Can not make entry shareable');
                }
            } catch (e) {
                logger.warn('Share Entry Link', e);
                throw e;
            } finally {
                setCreatingLink(false);
            }
        },
        [createShareableEntry]
    );

    return { createFantasyShareLink, creatingLink, shareLink };
};

export const useFantasyEntryImport = () => {
    const gqlClient = useClient();
    const { showAddLineupModal } = useAddLineup();
    const showLineupUnavailable = useLineupUnavailableSheet();

    const importSharedEntry = useCallback(
        async (shortID: string, socialLink?: SocialLink) => {
            try {
                if (socialLink?.payload.entryId) {
                    const response = await gqlClient.query<GetSharedEntryQuery, GetSharedEntryQueryVariables>(
                        GetSharedEntryDocument,
                        {
                            id: socialLink?.payload.entryId,
                        },
                        {
                            requestPolicy: 'network-only',
                        }
                    );
                    const entry = response.data?.getSharedEntry;
                    if (entry?.entry.status === Status.Settled) {
                        showLineupUnavailable();
                        return;
                    } else {
                        const noEventAcceptingBets = entry?.entry.picks.every(
                            p => !isEventAcceptingBets({ league: p.league, status: p.eventStatus })
                        );
                        if (noEventAcceptingBets) {
                            showLineupUnavailable();
                            return;
                        }
                    }
                    logger.debug(LOG_TAG, 'imported entry');
                    if (entry && socialLink) {
                        showAddLineupModal({
                            lineup: entry.entry.picks,
                            refferer: { entryId: entry.entry.id, linkId: shortID },
                        });
                    } else {
                        showLineupUnavailable();
                        return;
                    }
                } else {
                    showLineupUnavailable();
                }
            } catch (error) {
                showLineupUnavailable();
                logger.warn(LOG_TAG, error);
            }
        },
        [gqlClient, showAddLineupModal, showLineupUnavailable]
    );

    useHandleSocialLink(Product.Pickem, importSharedEntry);
};
